import { useEffect, useRef, useState } from "react";
/**
 * Hook that manages Airship push notification channel registration and opt-out.
 * Automatically registers the channel when consent is granted and browser permission is available.
 * Opts out when consent is revoked or browser permission is denied.
 *
 * @param hasPushConsent - Whether user has consented to push notifications
 * @param notificationPermission - Current browser notification permission status
 * @param sdk - Optional Airship SDK instance
 */
export function useAirshipChannel(hasPushConsent, notificationPermission, sdk) {
    const [isOptedIn, setIsOptedIn] = useState(undefined);
    const isManagingChannel = useRef(false);
    useEffect(() => {
        if (!sdk) {
            return;
        }
        if (isManagingChannel.current) {
            return;
        }
        const manageChannel = async () => {
            isManagingChannel.current = true;
            try {
                const optedIn = await sdk.channel.optedIn();
                const canRegister = await sdk.canRegister();
                const shouldRegister = hasPushConsent &&
                    notificationPermission === "granted" &&
                    canRegister &&
                    !optedIn;
                if (shouldRegister) {
                    await sdk.register();
                }
                const shouldOptOut = (!hasPushConsent || notificationPermission === "denied") && optedIn;
                if (shouldOptOut) {
                    await sdk.channel.optOut();
                }
                const finalOptedIn = await sdk.channel.optedIn();
                setIsOptedIn(finalOptedIn);
            }
            catch (error) {
                console.error("[Airship] Failed to manage push channel:", error);
                setIsOptedIn(undefined);
            }
            finally {
                isManagingChannel.current = false;
            }
        };
        manageChannel();
    }, [sdk, hasPushConsent, notificationPermission]);
    return { isOptedIn };
}
